import React, { useState, useEffect, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { CABANAS, DECORACOES } from "@/data/cabanas";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { ptBR } from "date-fns/locale";
import { format, differenceInCalendarDays } from "date-fns";
import {
    Calendar as CalendarIcon,
    Check,
    Loader2,
    CheckCircle2,
    PartyPopper,
    Home as HomeIcon,
    ArrowRight,
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtBRL = (v) =>
    v.toLocaleString("pt-BR", { style: "currency", currency: "BRL", minimumFractionDigits: 2 });

const PET_FEE = 200;

export default function Reservas() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const initialCabana = searchParams.get("cabana") || CABANAS[0].id;

    const [cabanaId, setCabanaId] = useState(initialCabana);
    const cabana = useMemo(() => CABANAS.find((c) => c.id === cabanaId) || CABANAS[0], [cabanaId]);

    const [checkIn, setCheckIn] = useState(null);
    const [checkOut, setCheckOut] = useState(null);

    const [form, setForm] = useState({
        nome_completo: "",
        data_nascimento: "",
        cpf: "",
        email: "",
        telefone: "",
        nome_acompanhante: "",
        numero_hospedes: 2,
        pet: false,
        decoracao_romantica: "nenhuma",
        forma_pagamento: "pix",
        parcelas: 1,
        solicitacao_especial: "",
    });

    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(null);

    const noites = useMemo(() => {
        if (!checkIn || !checkOut) return 0;
        const d = differenceInCalendarDays(checkOut, checkIn);
        return d > 0 ? d : 0;
    }, [checkIn, checkOut]);

    const decor = DECORACOES.find((d) => d.id === form.decoracao_romantica) || DECORACOES[0];

    const subtotalDiarias = noites * cabana.diaria;
    const taxaPet = form.pet ? PET_FEE : 0;
    const valorDecoracao = decor.preco || 0;
    const valorTotal = subtotalDiarias + taxaPet + valorDecoracao;

    const valorParcela = form.forma_pagamento === "cartao" ? valorTotal / form.parcelas : valorTotal;

    const handleChange = (key, value) => {
        setForm((f) => ({ ...f, [key]: value }));
    };

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const validate = () => {
        if (!checkIn || !checkOut) {
            toast.error("Selecione as datas de check-in e check-out");
            return false;
        }
        if (noites < 1) {
            toast.error("O check-out deve ser depois do check-in");
            return false;
        }
        if (!form.nome_completo.trim()) {
            toast.error("Informe seu nome completo");
            return false;
        }
        if (!form.data_nascimento) {
            toast.error("Informe sua data de nascimento");
            return false;
        }
        if (!form.cpf.trim()) {
            toast.error("Informe seu CPF");
            return false;
        }
        if (!form.email.trim() || !form.email.includes("@")) {
            toast.error("Informe um e-mail válido");
            return false;
        }
        if (!form.telefone.trim()) {
            toast.error("Informe seu telefone / WhatsApp");
            return false;
        }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;
        setSubmitting(true);
        try {
            const payload = {
                cabana_id: cabana.id,
                cabana_nome: cabana.nome,
                diaria: cabana.diaria,
                check_in: checkIn.toISOString(),
                check_out: checkOut.toISOString(),
                noites,
                ...form,
                valor_total: valorTotal,
            };
            const { data } = await axios.post(`${API}/bookings`, payload);
            setSuccess(data);
            window.scrollTo({ top: 0, behavior: "smooth" });
        } catch (err) {
            console.error(err);
            toast.error("Não foi possível enviar a reserva. Tente novamente.");
        } finally {
            setSubmitting(false);
        }
    };

    if (success) {
        return (
            <div className="bg-cdb-cream min-h-[80vh] flex items-center justify-center px-6 py-20">
                <div
                    data-testid="reserva-success"
                    className="max-w-2xl w-full bg-white border border-cdb-sand rounded-3xl p-10 sm:p-14 text-center reveal"
                >
                    <div className="w-20 h-20 rounded-full bg-cdb-forest/10 flex items-center justify-center mx-auto mb-6">
                        <CheckCircle2 className="w-12 h-12 text-cdb-forest" />
                    </div>
                    <PartyPopper className="w-8 h-8 text-cdb-gold mx-auto mb-2" />
                    <h1 className="text-3xl sm:text-4xl font-bold text-cdb-forest mb-4 leading-tight">
                        Sua reserva foi concluída com Sucesso!
                    </h1>
                    <p className="text-cdb-earth leading-relaxed mb-8">
                        Recebemos sua solicitação para a <span className="font-bold">{cabana.nome}</span>.
                        Os dados foram enviados via WhatsApp para nossa equipe e entraremos em contato em breve
                        para confirmar todos os detalhes.
                    </p>
                    <div className="bg-cdb-beige rounded-2xl p-6 text-left mb-8">
                        <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                                <p className="text-cdb-earth/60 text-xs uppercase tracking-wider mb-1">Código</p>
                                <p className="font-bold text-cdb-forest break-all">{success.id.slice(0, 8)}</p>
                            </div>
                            <div>
                                <p className="text-cdb-earth/60 text-xs uppercase tracking-wider mb-1">Valor total</p>
                                <p className="font-bold text-cdb-forest">{fmtBRL(valorTotal)}</p>
                            </div>
                            <div>
                                <p className="text-cdb-earth/60 text-xs uppercase tracking-wider mb-1">Check-in</p>
                                <p className="font-bold text-cdb-forest">
                                    {format(checkIn, "dd/MM/yyyy", { locale: ptBR })}
                                </p>
                            </div>
                            <div>
                                <p className="text-cdb-earth/60 text-xs uppercase tracking-wider mb-1">Check-out</p>
                                <p className="font-bold text-cdb-forest">
                                    {format(checkOut, "dd/MM/yyyy", { locale: ptBR })}
                                </p>
                            </div>
                        </div>
                    </div>
                    {!success.whatsapp_sent && (
                        <p className="text-xs text-cdb-earth/60 mb-4">
                            Atenção: tivemos um problema no envio automático via WhatsApp. Nossa equipe entrará em
                            contato manualmente.
                        </p>
                    )}
                    <button
                        data-testid="reserva-success-home"
                        onClick={() => navigate("/")}
                        className="inline-flex items-center gap-2 bg-cdb-forest hover:bg-cdb-forestDark text-white rounded-full px-8 py-3 font-bold transition-all"
                    >
                        <HomeIcon className="w-4 h-4" /> Voltar ao início
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-cdb-cream pb-24">
            <section className="pt-20 pb-10 sm:pt-28 leaf-pattern">
                <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
                    <span className="text-cdb-gold text-sm font-bold uppercase tracking-widest">Reserva</span>
                    <h1
                        data-testid="reservas-title"
                        className="text-4xl sm:text-5xl lg:text-6xl font-bold text-cdb-forest mt-3 mb-4 leading-tight"
                    >
                        Solicitar reserva
                    </h1>
                    <p className="text-cdb-earth/80 max-w-2xl mx-auto text-base sm:text-lg leading-relaxed">
                        Escolha a cabana, as datas e preencha seus dados. Enviaremos a solicitação direto para nossa
                        equipe via WhatsApp.
                    </p>
                </div>
            </section>

            <form
                onSubmit={handleSubmit}
                data-testid="reservas-form"
                className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-12 gap-8"
            >
                <div className="lg:col-span-8 space-y-8">
                    {/* Cabana */}
                    <Card title="1. Escolha sua cabana" testId="step-cabana">
                        <div className="grid sm:grid-cols-3 gap-3">
                            {CABANAS.map((c) => (
                                <button
                                    key={c.id}
                                    type="button"
                                    data-testid={`select-cabana-${c.id}`}
                                    onClick={() => setCabanaId(c.id)}
                                    className={`text-left rounded-2xl overflow-hidden border-2 transition-all ${
                                        cabanaId === c.id
                                            ? "border-cdb-forest ring-2 ring-cdb-forest/20"
                                            : "border-cdb-sand hover:border-cdb-gold"
                                    }`}
                                >
                                    <div className="aspect-[4/3] overflow-hidden">
                                        <img src={c.capa} alt={c.nome} className="w-full h-full object-cover" />
                                    </div>
                                    <div className="p-3">
                                        <p className="font-bold text-sm text-cdb-forest">{c.nome}</p>
                                        <p className="text-xs text-cdb-earth/70">{fmtBRL(c.diaria)} / noite</p>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </Card>

                    {/* Datas */}
                    <Card title="2. Escolha as datas" testId="step-datas">
                        <div className="grid sm:grid-cols-2 gap-4">
                            <DatePickerField
                                label="Check-in"
                                value={checkIn}
                                onChange={setCheckIn}
                                disabledBefore={today}
                                testId="datepicker-checkin"
                            />
                            <DatePickerField
                                label="Check-out"
                                value={checkOut}
                                onChange={setCheckOut}
                                disabledBefore={checkIn || today}
                                testId="datepicker-checkout"
                            />
                        </div>
                        {noites > 0 && (
                            <p
                                data-testid="noites-info"
                                className="mt-4 text-sm text-cdb-forest bg-cdb-beige rounded-xl px-4 py-3 inline-flex items-center gap-2"
                            >
                                <Check className="w-4 h-4" /> {noites} noite{noites > 1 ? "s" : ""} ×{" "}
                                {fmtBRL(cabana.diaria)} = {fmtBRL(subtotalDiarias)}
                            </p>
                        )}
                    </Card>

                    {/* Dados do hóspede */}
                    <Card title="3. Seus dados" testId="step-dados">
                        <div className="grid sm:grid-cols-2 gap-4">
                            <Input
                                label="Nome completo"
                                value={form.nome_completo}
                                onChange={(v) => handleChange("nome_completo", v)}
                                placeholder="Seu nome completo"
                                testId="input-nome"
                                required
                            />
                            <Input
                                label="Data de nascimento"
                                type="date"
                                value={form.data_nascimento}
                                onChange={(v) => handleChange("data_nascimento", v)}
                                testId="input-nascimento"
                                required
                            />
                            <Input
                                label="CPF"
                                value={form.cpf}
                                onChange={(v) => handleChange("cpf", v)}
                                placeholder="000.000.000-00"
                                testId="input-cpf"
                                required
                            />
                            <Input
                                label="E-mail"
                                type="email"
                                value={form.email}
                                onChange={(v) => handleChange("email", v)}
                                placeholder="voce@email.com"
                                testId="input-email"
                                required
                            />
                            <Input
                                label="Telefone (WhatsApp)"
                                value={form.telefone}
                                onChange={(v) => handleChange("telefone", v)}
                                placeholder="(21) 90000-0000"
                                testId="input-telefone"
                                required
                            />
                            <Input
                                label="Nome do acompanhante"
                                value={form.nome_acompanhante}
                                onChange={(v) => handleChange("nome_acompanhante", v)}
                                placeholder="Nome do(a) acompanhante"
                                testId="input-acompanhante"
                            />
                            <div>
                                <label className="block text-sm font-bold text-cdb-earth mb-2">
                                    Número de hóspedes
                                </label>
                                <Select
                                    value={String(form.numero_hospedes)}
                                    onValueChange={(v) => handleChange("numero_hospedes", parseInt(v, 10))}
                                >
                                    <SelectTrigger
                                        data-testid="select-hospedes"
                                        className="bg-white border-cdb-sand rounded-xl h-12"
                                    >
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {[1, 2, 3, 4].map((n) => (
                                            <SelectItem key={n} value={String(n)}>
                                                {n} hóspede{n > 1 ? "s" : ""}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-cdb-earth mb-2">
                                    Vai levar pet?
                                </label>
                                <div className="flex gap-2">
                                    {[
                                        { v: false, l: "Não" },
                                        { v: true, l: "Sim (+R$ 200)" },
                                    ].map((opt) => (
                                        <button
                                            key={String(opt.v)}
                                            type="button"
                                            data-testid={`pet-${opt.v ? "sim" : "nao"}`}
                                            onClick={() => handleChange("pet", opt.v)}
                                            className={`flex-1 h-12 rounded-xl font-bold text-sm transition-all border-2 ${
                                                form.pet === opt.v
                                                    ? "border-cdb-forest bg-cdb-forest text-white"
                                                    : "border-cdb-sand bg-white text-cdb-earth hover:border-cdb-gold"
                                            }`}
                                        >
                                            {opt.l}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </Card>

                    {/* Decoração */}
                    <Card title="4. Decoração romântica (opcional)" testId="step-decoracao">
                        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                            {DECORACOES.map((d) => {
                                const selected = form.decoracao_romantica === d.id;
                                return (
                                    <button
                                        key={d.id}
                                        type="button"
                                        data-testid={`decor-${d.id}`}
                                        onClick={() => handleChange("decoracao_romantica", d.id)}
                                        className={`text-left rounded-2xl overflow-hidden border-2 transition-all ${
                                            selected
                                                ? "border-cdb-forest ring-2 ring-cdb-forest/20"
                                                : "border-cdb-sand hover:border-cdb-gold"
                                        }`}
                                    >
                                        {d.imagem ? (
                                            <div className="aspect-[4/3] overflow-hidden">
                                                <img src={d.imagem} alt={d.nome} className="w-full h-full object-cover" />
                                            </div>
                                        ) : (
                                            <div className="aspect-[4/3] bg-cdb-beige flex items-center justify-center">
                                                <span className="text-cdb-earth/40 text-sm">Sem decoração</span>
                                            </div>
                                        )}
                                        <div className="p-3">
                                            <p className="font-bold text-sm text-cdb-forest">{d.nome}</p>
                                            <p className="text-xs text-cdb-earth/70">
                                                {d.preco === 0 ? "Grátis" : `+ ${fmtBRL(d.preco)}`}
                                            </p>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                        {decor && decor.descricao && decor.preco > 0 && (
                            <p className="text-xs text-cdb-earth/80 mt-3 leading-relaxed">
                                <span className="font-bold">{decor.nome}:</span> {decor.descricao}
                            </p>
                        )}
                    </Card>

                    {/* Pagamento */}
                    <Card title="5. Forma de pagamento" testId="step-pagamento">
                        <div className="grid sm:grid-cols-2 gap-3 mb-4">
                            <button
                                type="button"
                                data-testid="pagamento-pix"
                                onClick={() => handleChange("forma_pagamento", "pix")}
                                className={`h-14 rounded-xl font-bold transition-all border-2 ${
                                    form.forma_pagamento === "pix"
                                        ? "border-cdb-forest bg-cdb-forest text-white"
                                        : "border-cdb-sand bg-white text-cdb-earth hover:border-cdb-gold"
                                }`}
                            >
                                PIX
                            </button>
                            <button
                                type="button"
                                data-testid="pagamento-cartao"
                                onClick={() => handleChange("forma_pagamento", "cartao")}
                                className={`h-14 rounded-xl font-bold transition-all border-2 ${
                                    form.forma_pagamento === "cartao"
                                        ? "border-cdb-forest bg-cdb-forest text-white"
                                        : "border-cdb-sand bg-white text-cdb-earth hover:border-cdb-gold"
                                }`}
                            >
                                Cartão de Crédito
                            </button>
                        </div>

                        {form.forma_pagamento === "cartao" && (
                            <div>
                                <label className="block text-sm font-bold text-cdb-earth mb-2">
                                    Número de parcelas (sem juros)
                                </label>
                                <Select
                                    value={String(form.parcelas)}
                                    onValueChange={(v) => handleChange("parcelas", parseInt(v, 10))}
                                >
                                    <SelectTrigger
                                        data-testid="select-parcelas"
                                        className="bg-white border-cdb-sand rounded-xl h-12"
                                    >
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {[1, 2, 3, 4, 5, 6].map((p) => (
                                            <SelectItem key={p} value={String(p)}>
                                                {p}x de {fmtBRL(valorTotal / p)} sem juros
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}

                        <div className="mt-6">
                            <label className="block text-sm font-bold text-cdb-earth mb-2">
                                Alguma solicitação especial?
                            </label>
                            <textarea
                                data-testid="input-solicitacao"
                                value={form.solicitacao_especial}
                                onChange={(e) => handleChange("solicitacao_especial", e.target.value)}
                                rows={4}
                                placeholder="Comemorações, restrições, preferências..."
                                className="w-full bg-white border border-cdb-sand rounded-xl px-4 py-3 focus:ring-2 focus:ring-cdb-forest outline-none text-cdb-earth"
                            />
                        </div>
                    </Card>
                </div>

                {/* Resumo */}
                <aside className="lg:col-span-4">
                    <div
                        data-testid="reservas-summary"
                        className="bg-white border border-cdb-sand rounded-3xl p-6 lg:p-8 lg:sticky lg:top-24"
                    >
                        <h3 className="text-xl font-bold text-cdb-forest mb-1">Resumo da reserva</h3>
                        <p className="text-sm text-cdb-earth/70 mb-6">{cabana.nome}</p>

                        <div className="space-y-3 text-sm border-b border-cdb-sand pb-4 mb-4">
                            <Row label="Check-in" value={checkIn ? format(checkIn, "dd/MM/yyyy", { locale: ptBR }) : "—"} />
                            <Row label="Check-out" value={checkOut ? format(checkOut, "dd/MM/yyyy", { locale: ptBR }) : "—"} />
                            <Row label="Noites" value={noites || "—"} />
                            <Row label={`${noites || 0} × ${fmtBRL(cabana.diaria)}`} value={fmtBRL(subtotalDiarias)} />
                            {form.pet && <Row label="Taxa pet" value={fmtBRL(PET_FEE)} />}
                            {valorDecoracao > 0 && (
                                <Row label={`Decoração ${decor.nome}`} value={fmtBRL(valorDecoracao)} />
                            )}
                        </div>

                        <div className="flex items-baseline justify-between mb-2">
                            <span className="text-sm font-bold text-cdb-earth">Total</span>
                            <span
                                data-testid="valor-total"
                                className="text-3xl font-bold text-cdb-forest"
                            >
                                {fmtBRL(valorTotal)}
                            </span>
                        </div>
                        {form.forma_pagamento === "cartao" && form.parcelas > 1 && valorTotal > 0 && (
                            <p className="text-xs text-cdb-earth/70 mb-4">
                                ou {form.parcelas}x de {fmtBRL(valorParcela)} sem juros
                            </p>
                        )}

                        <button
                            type="submit"
                            disabled={submitting}
                            data-testid="reservas-submit"
                            className="w-full mt-4 inline-flex items-center justify-center gap-2 bg-cdb-gold hover:bg-cdb-goldDark disabled:opacity-60 text-white rounded-full px-6 py-4 font-bold transition-all shadow-md hover:shadow-lg"
                        >
                            {submitting ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" /> Enviando...
                                </>
                            ) : (
                                <>
                                    Confirmar reserva <ArrowRight className="w-4 h-4" />
                                </>
                            )}
                        </button>
                        <p className="text-xs text-cdb-earth/60 text-center mt-3 leading-relaxed">
                            Não cobramos pagamento online. Os dados são enviados via WhatsApp para nossa equipe.
                        </p>
                    </div>
                </aside>
            </form>
        </div>
    );
}

function Card({ title, children, testId }) {
    return (
        <div
            data-testid={testId}
            className="bg-white border border-cdb-sand rounded-3xl p-6 sm:p-8"
        >
            <h2 className="text-xl sm:text-2xl font-bold text-cdb-forest mb-6">{title}</h2>
            {children}
        </div>
    );
}

function Input({ label, value, onChange, placeholder, type = "text", required, testId }) {
    return (
        <div>
            <label className="block text-sm font-bold text-cdb-earth mb-2">
                {label} {required && <span className="text-cdb-gold">*</span>}
            </label>
            <input
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                data-testid={testId}
                className="w-full h-12 bg-white border border-cdb-sand rounded-xl px-4 focus:ring-2 focus:ring-cdb-forest focus:border-transparent outline-none text-cdb-earth"
            />
        </div>
    );
}

function Row({ label, value }) {
    return (
        <div className="flex justify-between text-cdb-earth">
            <span className="text-cdb-earth/70">{label}</span>
            <span className="font-bold">{value}</span>
        </div>
    );
}

function DatePickerField({ label, value, onChange, disabledBefore, testId }) {
    const [open, setOpen] = useState(false);
    return (
        <div>
            <label className="block text-sm font-bold text-cdb-earth mb-2">{label}</label>
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <button
                        type="button"
                        data-testid={testId}
                        className="w-full h-12 bg-white border border-cdb-sand rounded-xl px-4 text-left flex items-center justify-between hover:border-cdb-gold transition-colors"
                    >
                        <span className={value ? "text-cdb-earth" : "text-cdb-earth/40"}>
                            {value ? format(value, "dd 'de' MMMM, yyyy", { locale: ptBR }) : "Selecionar data"}
                        </span>
                        <CalendarIcon className="w-4 h-4 text-cdb-forest" />
                    </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                        mode="single"
                        selected={value}
                        onSelect={(d) => {
                            onChange(d);
                            setOpen(false);
                        }}
                        locale={ptBR}
                        disabled={(date) => {
                            const min = disabledBefore || new Date();
                            const mz = new Date(min);
                            mz.setHours(0, 0, 0, 0);
                            const dz = new Date(date);
                            dz.setHours(0, 0, 0, 0);
                            return dz < mz;
                        }}
                        initialFocus
                    />
                </PopoverContent>
            </Popover>
        </div>
    );
}
